import {
  PANDERR_TEMPLATE_REGISTRY,
  getPanderrTemplate,
  PROFESSIONAL_MODERN_TEMPLATE,
  renderPanderrDocument,
  renderPanderrPageDocument,
} from "./studio/design-system/index.ts";

import { adaptTemplatePageContent } from "./studio/design-system/content-adapter.ts";
import { runCapturedUrlImportWorkflow } from "./studio/import/website-import-workflow.ts";
import type { CapturedUrlSite } from "./studio/import/url-import-adapter.ts";

import type {
  PanderrTemplateDefinition,
  PanderrTemplatePage,
} from "./studio/design-system/types.ts";

import type { StudioPage, StudioProject } from "./studio/types/project.ts";
import type { SourceAssetCandidate } from "./studio/types/asset.ts";

interface BusinessPreviewData {
  name?: string;
  type?: string;
  phone?: string;
  email?: string;
  address?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zip?: string;
  services?: string;
  description?: string;
  logoUrl?: string;
  websiteUrl?: string;
  defaultMetaDescription?: string;
  serviceArea?: string;
  businessHours?: string;
  facebookUrl?: string;
  instagramUrl?: string;
  linkedinUrl?: string;
  youtubeUrl?: string;
}

interface BrowserTemplateSummary {
  id: string;
  name: string;
  category: string;
  description: string;
  previewImage: string;
}

interface BrowserRenderedPage {
  id: string;
  name: string;
  title: string;
  slug: string;
  renderedDocument: string;
}

interface BrowserRenderedSite {
  renderedDocument: string;
  renderedPages: BrowserRenderedPage[];
}

declare global {
  interface Window {
    PanderrDesignSystemV1?: {
      templates: BrowserTemplateSummary[];

      getTemplates: () => BrowserTemplateSummary[];

      renderTemplate: (
        templateId: string,
        business?: BusinessPreviewData,
      ) => string;

      renderTemplateSite: (
        templateId: string,
        business?: BusinessPreviewData,
        project?: Partial<StudioProject>,
      ) => BrowserRenderedSite;

      analyzeCapturedSite: (capture: CapturedUrlSite) => {
        pages: StudioPage[];
        sourceAssets: SourceAssetCandidate[];
      };

      normalizeCapturedSite: (capture: CapturedUrlSite) => StudioPage[];

      renderProfessionalModern: (business?: BusinessPreviewData) => string;
    };
  }
}

function cloneTemplate(templateId: string): PanderrTemplateDefinition {
  const template = getPanderrTemplate(templateId);

  if (!template) {
    throw new Error(`Unknown Panderr template: ${templateId}`);
  }

  return structuredClone(template);
}

function serviceTitles(value: string | undefined): string[] {
  return String(value || "")
    .split(/\n|,/)
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 3);
}

function projectBusiness(
  project: Partial<StudioProject> | undefined,
): BusinessPreviewData {
  const settings = project?.websiteSettings;

  if (!settings) {
    return {};
  }

  return {
    name: settings.businessName,
    logoUrl: settings.logoUrl,
    websiteUrl: settings.websiteUrl,
    defaultMetaDescription: settings.defaultMetaDescription,
    phone: settings.phone,
    email: settings.email,
    address: settings.streetAddress,
    addressLine2: settings.addressLine2,
    city: settings.city,
    state: settings.state,
    zip: settings.zip,
    description: settings.businessDescription,
    serviceArea: settings.serviceArea,
    businessHours: settings.businessHours,
    facebookUrl: settings.facebookUrl,
    instagramUrl: settings.instagramUrl,
    linkedinUrl: settings.linkedinUrl,
    youtubeUrl: settings.youtubeUrl,
  };
}

function businessSiteData(
  business: BusinessPreviewData,
  project?: Partial<StudioProject>,
) {
  return {
    businessName: business.name,
    logoUrl: business.logoUrl,
    websiteUrl: business.websiteUrl,
    defaultMetaDescription: business.defaultMetaDescription,
    businessDescription: business.description,
    streetAddress: business.address,
    addressLine2: business.addressLine2,
    city: business.city,
    state: business.state,
    zip: business.zip,
    serviceArea: business.serviceArea,
    phone: business.phone,
    email: business.email,
    businessHours: business.businessHours,
    facebookUrl: business.facebookUrl,
    instagramUrl: business.instagramUrl,
    linkedinUrl: business.linkedinUrl,
    youtubeUrl: business.youtubeUrl,

    managedContentProjectId: project?.id,
  };
}

function mergeBusinessData(
  explicitBusiness: BusinessPreviewData,
  project: Partial<StudioProject> | undefined,
): BusinessPreviewData {
  return {
    ...projectBusiness(project),
    ...Object.fromEntries(
      Object.entries(explicitBusiness).filter(
        ([, value]) => String(value || "").trim() !== "",
      ),
    ),
  };
}

function personalizeTemplateDefinition(
  template: PanderrTemplateDefinition,
  business: BusinessPreviewData = {},
): PanderrTemplateDefinition {
  const personalized = structuredClone(template);

  const name = String(business.name || "").trim() || "Your Business";
  const description = String(business.description || "").trim();
  const type = String(business.type || "").trim();
  const services = serviceTitles(business.services);

  const hero = personalized.sections.find((section) => section.type === "hero");

  if (hero) {
    if (type) {
      hero.eyebrow = type;
    }

    if (business.name) {
      hero.heading = name;
    }

    if (description) {
      hero.body = description;
    }
  }

  const servicesSection = personalized.sections.find(
    (section) => section.type === "services",
  );

  if (servicesSection && services.length) {
    servicesSection.services = services.map((title) => ({
      title,
      description:
        "Professional service tailored to your needs. Edit this description in Studio.",
      href: "/services/",
    }));
  }

  const about = personalized.sections.find(
    (section) => section.type === "about",
  );

  if (about) {
    if (business.name) {
      about.heading = `About ${name}`;
    }

    if (description) {
      about.body = description;
    }
  }

  const fullAddress = [
    business.address,
    business.city,
    business.state,
    business.zip,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(", ");

  const cta = personalized.sections.find((section) => section.type === "cta");

  if (cta && fullAddress) {
    cta.body = `Serving customers from ${fullAddress}.`;
  }

  return personalized;
}

function replaceBusinessFallbacks(
  document: string,
  business: BusinessPreviewData,
): string {
  let output = document;

  if (business.name) {
    output = output.replaceAll("Your Business", String(business.name).trim());
  }

  if (business.phone) {
    const phone = String(business.phone).trim();

    output = output
      .replaceAll("(555) 555-5555", phone)
      .replaceAll("tel:+15555555555", `tel:${phone.replace(/[^\d+]/g, "")}`);
  }

  if (business.email) {
    const email = String(business.email).trim();

    output = output
      .replaceAll("hello@example.com", email)
      .replaceAll("mailto:hello@example.com", `mailto:${email}`);
  }

  return output;
}

function personalizeTemplate(
  templateId: string,
  business: BusinessPreviewData = {},
): string {
  const template = personalizeTemplateDefinition(
    cloneTemplate(templateId),
    business,
  );

  return replaceBusinessFallbacks(renderPanderrDocument(
    template,
    businessSiteData(business),
  ), business);
}

function normalizeRoute(value: string | undefined): string {
  const raw = String(value || "")
    .trim()
    .toLowerCase();

  if (!raw || raw === "/") {
    return "/";
  }

  let pathname = raw;

  try {
    pathname = new URL(raw, "https://panderr.local").pathname;
  } catch {
    pathname = raw;
  }

  pathname = pathname
    .replace(/\/index\.html?$/i, "/")
    .replace(/^\/+/, "")
    .replace(/\/+$/, "");

  return pathname ? `/${pathname}/` : "/";
}

function normalizedText(value: string | undefined): string {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function inferTemplatePageRole(page: PanderrTemplatePage): StudioPage["role"] {
  const value = [page.id, page.name, page.title, page.slug]
    .join(" ")
    .toLowerCase();

  if (value.includes("about")) return "about";
  if (value.includes("service")) return "services";
  if (value.includes("contact")) return "contact";
  if (value.includes("gallery")) return "gallery";
  if (value.includes("portfolio") || value.includes("project")) {
    return "portfolio";
  }
  if (value.includes("testimonial") || value.includes("review")) {
    return "testimonials";
  }
  if (value.includes("faq")) return "faq";
  if (value.includes("blog") || value.includes("news")) return "blog";
  if (value.includes("pricing")) return "pricing";
  if (value.includes("team")) return "team";

  return "other";
}

function findSourcePage(
  templatePage: PanderrTemplatePage,
  pages: StudioPage[],
): StudioPage | undefined {
  const targetRoute = normalizeRoute(templatePage.slug);

  const routeMatch = pages.find((page) => {
    const pageRoute = normalizeRoute(page.content?.sourceRoute || page.slug);

    return pageRoute === targetRoute;
  });

  if (routeMatch) {
    return routeMatch;
  }

  const targetRole = inferTemplatePageRole(templatePage);

  if (targetRole !== "other") {
    const roleMatch = pages.find((page) => page.role === targetRole);

    if (roleMatch) {
      return roleMatch;
    }
  }

  const targetNames = new Set(
    [templatePage.id, templatePage.name, templatePage.title]
      .map(normalizedText)
      .filter(Boolean),
  );

  return pages.find((page) =>
    [
      page.id,
      page.name,
      page.title,
      page.navigationLabel,
      page.content?.sourceTitle,
    ]
      .map(normalizedText)
      .some((value) => value && targetNames.has(value)),
  );
}

function findHomepage(pages: StudioPage[]): StudioPage | undefined {
  return (
    pages.find((page) => page.isHomePage) ||
    pages.find((page) => page.role === "home") ||
    pages.find(
      (page) => normalizeRoute(page.content?.sourceRoute || page.slug) === "/",
    )
  );
}

function sourcePageKey(page: StudioPage): string {
  return normalizeRoute(page.content?.sourceRoute || page.slug);
}

function sourcePageTitle(page: StudioPage): string {
  return (
    String(page.content?.sourceTitle || "").trim() ||
    String(page.title || "").trim() ||
    String(page.name || "").trim() ||
    "Page"
  );
}

function sourcePageBody(page: StudioPage): string {
  const sections = page.content?.sections || [];

  const text = sections
    .flatMap((section) =>
      section.blocks.flatMap((block) => {
        switch (block.type) {
          case "paragraph":
          case "quote":
            return [String(block.text || "").trim()];

          case "list":
            return block.items.map((item) => String(item || "").trim());

          case "link":
            return [String(block.link.text || "").trim()];

          default:
            return [];
        }
      }),
    )
    .filter(Boolean);

  return text.join("\n\n");
}

function createFallbackTemplatePage(
  sourcePage: StudioPage,
): PanderrTemplatePage {
  const title = sourcePageTitle(sourcePage);
  const slug = sourcePageKey(sourcePage);
  const role = sourcePage.role || "other";

  if (role === "faq") {
    return {
      id: sourcePage.id,
      name: sourcePage.name || title,
      title,
      slug,
      sections: [
        {
          id: `${sourcePage.id}-hero`,
          type: "hero",
          eyebrow: "Frequently asked questions",
          heading: title,
          body:
            sourcePageBody(sourcePage) ||
            "Find answers to common questions below.",
        },
        {
          id: `${sourcePage.id}-faq`,
          type: "faq",
          eyebrow: "Questions & answers",
          heading: "Frequently asked questions",
          body:
            sourcePageBody(sourcePage) ||
            "Add or update your frequently asked questions in Studio.",
        },
      ],
    };
  }

  return {
    id: sourcePage.id,
    name: sourcePage.name || title,
    title,
    slug,
    sections: [
      {
        id: `${sourcePage.id}-hero`,
        type: "hero",
        eyebrow: role === "legal" ? "Legal information" : undefined,
        heading: title,
        body: sourcePageBody(sourcePage),
      },
    ],
  };
}

function renderTemplateSite(
  templateId: string,
  business: BusinessPreviewData = {},
  project?: Partial<StudioProject>,
): BrowserRenderedSite {
  const effectiveBusiness = mergeBusinessData(business, project);

  const template = personalizeTemplateDefinition(
    cloneTemplate(templateId),
    effectiveBusiness,
  );

  const sourcePages = Array.isArray(project?.pages) ? project.pages : [];

  const sourceHomepage = findHomepage(sourcePages);

  const homepageTemplatePage: PanderrTemplatePage = {
    id: "home",
    name: "Home",
    title: template.name,
    slug: "/",
    sections: template.sections,
  };

  const adaptedHomepage = adaptTemplatePageContent(
    homepageTemplatePage,
    sourceHomepage?.content,
  );

  const adaptedHomepageTemplate: PanderrTemplateDefinition = {
    ...template,
    sections: adaptedHomepage.sections,
  };

  const renderedDocument = replaceBusinessFallbacks(
    renderPanderrDocument(
      adaptedHomepageTemplate,
      businessSiteData(effectiveBusiness, project),
    ),
    effectiveBusiness,
  );

  const sourceInteriorPages = sourcePages.filter(
    (page) => !page.isHomePage && page.role !== "home",
  );

  const renderedPages =
    sourceInteriorPages.length > 0
      ? sourceInteriorPages.map((sourcePage) => {
          const matchingTemplatePage = (template.pages || []).find((page) => {
            const matchedSourcePage = findSourcePage(page, [sourcePage]);
            return matchedSourcePage?.id === sourcePage.id;
          });

          const presentationPage = matchingTemplatePage
            ? adaptTemplatePageContent(
                {
                  ...matchingTemplatePage,
                  id: sourcePage.id,
                  name: sourcePage.name || matchingTemplatePage.name,
                  title:
                    sourcePage.title ||
                    sourcePage.content?.sourceTitle ||
                    matchingTemplatePage.title,
                  slug: sourcePageKey(sourcePage),
                },
                sourcePage.content,
              )
            : createFallbackTemplatePage(sourcePage);

          return {
            id: sourcePage.id,
            name: sourcePage.name || presentationPage.name,
            title:
              sourcePage.title ||
              sourcePage.content?.sourceTitle ||
              presentationPage.title,
            slug: sourcePageKey(sourcePage),
            renderedDocument: replaceBusinessFallbacks(
              renderPanderrPageDocument(
                template,
                presentationPage,
                businessSiteData(effectiveBusiness, project),
              ),
              effectiveBusiness,
            ),
          };
        })
      : (template.pages || []).map((page) => {
          return {
            id: page.id,
            name: page.name,
            title: page.title,
            slug: page.slug,
            renderedDocument: replaceBusinessFallbacks(
              renderPanderrPageDocument(
                template,
                page,
                businessSiteData(effectiveBusiness, project),
              ),
              effectiveBusiness,
            ),
          };
        });

  return {
    renderedDocument,
    renderedPages,
  };
}

function inferSourcePageRole(route: string, title: string): StudioPage["role"] {
  const value = `${route} ${title}`.toLowerCase();

  if (normalizeRoute(route) === "/") return "home";
  if (value.includes("about")) return "about";
  if (value.includes("service")) return "services";
  if (value.includes("contact")) return "contact";
  if (value.includes("gallery")) return "gallery";
  if (value.includes("portfolio") || value.includes("project")) {
    return "portfolio";
  }
  if (value.includes("testimonial") || value.includes("review")) {
    return "testimonials";
  }
  if (value.includes("faq")) return "faq";
  if (value.includes("blog") || value.includes("news")) return "blog";
  if (value.includes("pricing")) return "pricing";
  if (value.includes("team")) return "team";

  return "other";
}

function analyzeCapturedSite(capture: CapturedUrlSite): {
  pages: StudioPage[];
  sourceAssets: SourceAssetCandidate[];
} {
  const result = runCapturedUrlImportWorkflow(capture);

  const pages = result.content.map((analysis, index) => {
    const route = normalizeRoute(
      analysis.content.sourceRoute || analysis.route,
    );

    const title =
      String(analysis.content.sourceTitle || "").trim() || `Page ${index + 1}`;

    const isHomePage = route === "/";

    return {
      id: analysis.pageId || (isHomePage ? "home" : `page-${index + 1}`),
      name: isHomePage ? "Home" : title,
      navigationLabel: isHomePage ? "Home" : title,
      title,
      slug: route,
      isHomePage,
      role: inferSourcePageRole(route, title),
      content: analysis.content,
    } as StudioPage;
  });

  const sourceAssets: SourceAssetCandidate[] = result.remoteAssetCandidates.map(
    (candidate) => ({
      id: candidate.id,
      url: candidate.url,
      altText: candidate.altText,
      width: candidate.width,
      height: candidate.height,
      sourcePages: [...candidate.sourcePages],
    }),
  );

  return {
    pages,
    sourceAssets,
  };
}

function normalizeCapturedSite(capture: CapturedUrlSite): StudioPage[] {
  return analyzeCapturedSite(capture).pages;
}

const templateSummaries: BrowserTemplateSummary[] =
  PANDERR_TEMPLATE_REGISTRY.map(
    ({ id, name, category, description, previewImage }) => ({
      id,
      name,
      category,
      description,
      previewImage,
    }),
  );

window.PanderrDesignSystemV1 = {
  templates: templateSummaries,

  getTemplates() {
    return [...templateSummaries];
  },

  renderTemplate(templateId, business = {}) {
    return personalizeTemplate(templateId, business);
  },

  renderTemplateSite(templateId, business = {}, project) {
    return renderTemplateSite(templateId, business, project);
  },

  analyzeCapturedSite(capture) {
    return analyzeCapturedSite(capture);
  },

  normalizeCapturedSite(capture) {
    return normalizeCapturedSite(capture);
  },

  renderProfessionalModern(business = {}) {
    return personalizeTemplate(PROFESSIONAL_MODERN_TEMPLATE.id, business);
  },
};
